import type { WidgetDefinitionInput } from "@cd/core";

export type Template = WidgetDefinitionInput & { blurb: string };

/** The slice of the catalog a template is checked against (structural, so tests can pass a registry's catalog). */
export interface TemplateCatalog {
  metrics: { ref: string }[];
  dimensions: { ref: string }[];
  compatibility: Record<string, string[]>;
}

/**
 * Curated starting points (M3 turns this into an internal template gallery). Each data source has its
 * own models, so there is a set per source; `templatesFor` shows only the ones the current catalog can run.
 */
const FIXTURE_TEMPLATES: Template[] = [
  {
    title: "Net sales", blurb: "Headline number vs the previous period",
    query: { metrics: [{ kind: "metric", ref: "order_items.net_sales" }], compareTo: "previous_period" },
    visualization: { type: "kpi", config: {} },
  },
  {
    title: "Average check", blurb: "Net sales ÷ orders, computed from totals",
    query: {
      metrics: [{ kind: "formula", id: "avg_check", label: "Average check", expr: "order_items.net_sales / orders.order_count", format: "currency" }],
      compareTo: "previous_period",
    },
    visualization: { type: "kpi", config: {} },
  },
  {
    title: "Daily sales trend", blurb: "Net sales by day, with last period overlaid",
    query: { metrics: [{ kind: "metric", ref: "order_items.net_sales" }], dimensions: [{ ref: "orders.opened_at", grain: "day" }], compareTo: "previous_period" },
    visualization: { type: "area", config: {} },
  },
  {
    title: "Sales by category", blurb: "Share of net sales across menu categories",
    query: { metrics: [{ kind: "metric", ref: "order_items.net_sales" }], dimensions: [{ ref: "products.category" }] },
    visualization: { type: "pie", config: {} },
  },
  {
    title: "Top products", blurb: "Best sellers by revenue and units",
    query: {
      metrics: [{ kind: "metric", ref: "order_items.net_sales" }, { kind: "metric", ref: "order_items.items_sold" }],
      dimensions: [{ ref: "products.name" }],
      sort: [{ key: "order_items.net_sales", dir: "desc" }],
      limit: 10,
    },
    visualization: { type: "table", config: {} },
  },
  {
    title: "Busiest hours", blurb: "Orders by day of week and hour",
    query: { metrics: [{ kind: "metric", ref: "orders.order_count" }], dimensions: [{ ref: "orders.day_of_week" }, { ref: "orders.hour_of_day" }] },
    visualization: { type: "pivot", config: {} },
  },
  {
    title: "Tips by payment method", blurb: "Where gratuity comes from",
    query: { metrics: [{ kind: "metric", ref: "payments.tips" }], dimensions: [{ ref: "payments.method" }] },
    visualization: { type: "bar", config: { horizontal: true } },
  },
  {
    title: "Sales by channel over time", blurb: "Dine-in vs takeout vs delivery by week",
    query: { metrics: [{ kind: "metric", ref: "order_items.net_sales" }], dimensions: [{ ref: "orders.opened_at", grain: "week" }, { ref: "orders.channel" }] },
    visualization: { type: "bar", config: { stacked: true } },
  },
];

/** GoTab data (the GoTab pack in GoTab-Inc/dashboardPacks): the certified sales model over v2.sales_daily. */
const GOTAB_TEMPLATES: Template[] = [
  {
    title: "Net sales", blurb: "Headline number vs the previous period",
    query: { metrics: [{ kind: "metric", ref: "sales.net_sales" }], compareTo: "previous_period" },
    visualization: { type: "kpi", config: {} },
  },
  {
    title: "Daily sales trend", blurb: "Net sales by business day, with last period overlaid",
    query: { metrics: [{ kind: "metric", ref: "sales.net_sales" }], dimensions: [{ ref: "sales.fiscal_day", grain: "day" }], compareTo: "previous_period" },
    visualization: { type: "area", config: {} },
  },
  {
    title: "Sales summary", blurb: "Gross sales, comps, net sales, tax and tips",
    query: {
      metrics: [
        { kind: "metric", ref: "sales.gross_sales" },
        { kind: "metric", ref: "sales.comps_and_refunds" },
        { kind: "metric", ref: "sales.net_sales" },
        { kind: "metric", ref: "sales.tax" },
        { kind: "metric", ref: "sales.tips" },
      ],
    },
    visualization: { type: "table", config: {} },
  },
  {
    title: "Tip %", blurb: "Tips ÷ net sales, computed from totals",
    query: {
      metrics: [{ kind: "formula", id: "tip_pct", label: "Tip %", expr: "sales.tips / sales.net_sales * 100", format: "percent" }],
      compareTo: "previous_period",
    },
    visualization: { type: "kpi", config: {} },
  },
  {
    title: "Sales by category", blurb: "Share of net sales across menu categories",
    query: { metrics: [{ kind: "metric", ref: "sales.net_sales" }], dimensions: [{ ref: "products.category" }] },
    visualization: { type: "pie", config: {} },
  },
  {
    title: "Top products", blurb: "Best sellers by net sales",
    query: {
      metrics: [{ kind: "metric", ref: "sales.net_sales" }],
      dimensions: [{ ref: "products.name" }],
      sort: [{ key: "sales.net_sales", dir: "desc" }],
      limit: 10,
    },
    visualization: { type: "table", config: {} },
  },
  {
    title: "Sales by hour", blurb: "Net sales by hour of day",
    query: { metrics: [{ kind: "metric", ref: "sales.net_sales" }], dimensions: [{ ref: "sales.hour" }], sort: [{ key: "sales.hour", dir: "asc" }] },
    visualization: { type: "bar", config: {} },
  },
  {
    title: "Weekly sales by location", blurb: "Net sales per week, stacked by location",
    query: { metrics: [{ kind: "metric", ref: "sales.net_sales" }], dimensions: [{ ref: "sales.fiscal_day", grain: "week" }, { ref: "locations.name" }] },
    visualization: { type: "bar", config: { stacked: true } },
  },
  {
    title: "Chart of accounts", blurb: "Debit, credit and net from the ledger, by account type and account",
    query: {
      metrics: [{ kind: "metric", ref: "ledger.debit" }, { kind: "metric", ref: "ledger.credit" }, { kind: "metric", ref: "ledger.net" }],
      dimensions: [{ ref: "ledger.account_type" }, { ref: "ledger.account" }],
      sort: [{ key: "ledger.account_type", dir: "asc" }, { key: "ledger.account", dir: "asc" }],
    },
    visualization: { type: "table", config: {} },
  },
];

export const TEMPLATES: Template[] = [...FIXTURE_TEMPLATES, ...GOTAB_TEMPLATES];

const REF = /[a-z_][a-z0-9_]*\.[a-z_][a-z0-9_]*/g;

/**
 * Templates the catalog can run: every metric (including those inside formulas) exists, and every
 * grouping and filter exists and is compatible with each of those metrics.
 */
export function templatesFor(catalog: TemplateCatalog, templates: Template[] = TEMPLATES): Template[] {
  const metrics = new Set(catalog.metrics.map((m) => m.ref));
  const dimensions = new Set(catalog.dimensions.map((d) => d.ref));
  return templates.filter(({ query }) => {
    const metricRefs = query.metrics.flatMap((m) => (m.kind === "metric" ? [m.ref] : m.expr.match(REF) ?? []));
    const dimRefs = [...(query.dimensions ?? []), ...(query.filters ?? [])].map((d) => d.ref);
    return (
      metricRefs.length > 0 &&
      metricRefs.every((r) => metrics.has(r)) &&
      dimRefs.every((d) => dimensions.has(d) && metricRefs.every((r) => catalog.compatibility[r]?.includes(d)))
    );
  });
}
